/**
 * Which machines serve which project, right now (RFC 0290 §3d, S5).
 *
 * The same shape of question `useWorldDevices` already asks of the same
 * document, so it reuses that hook rather than opening presence a second time:
 * availability is a read of presence, not a thing of its own.
 */

import { availabilityIn, type DevicePresenceRef } from '@mod/core/model'
import { useCallback } from 'react'
import type { ProjectHosting } from '../sessions/projectChoices'
import { useWorldDevices } from './useWorldDevices'

/**
 * A lookup rather than a map, because the caller has the project list and this
 * does not: building a map here would mean this hook deciding which projects
 * are worth asking about.
 */
export function useProjectHosting(): (projectId: string) => ProjectHosting {
  const devices = useWorldDevices()
  return useCallback(
    (projectId: string): ProjectHosting => {
      // The three-state rule is mod-core's, not this hook's: re-deciding it
      // here is how the `unknown` case drifts and greys out the whole picker.
      const availability = availabilityIn(devices, projectId)
      const hosts =
        availability === 'available'
          ? devices
              .filter((d: DevicePresenceRef) =>
                (d.mounts ?? []).some((m) => m.projectId === projectId),
              )
              .map((d) => d.name || d.deviceId)
          : []
      return { availability, hosts }
    },
    [devices],
  )
}
